/**
 * Client-side highlight of the words a landlord searched for (design brief: Search results). The server
 * ranks and returns the excerpt; this only marks where the query words occur in the text it shows.
 */

export interface HighlightPart {
  text: string;
  match: boolean;
}

const MIN_TOKEN_LENGTH = 2;

/** The distinct, lower-cased words of a query that are long enough to be worth marking. */
export function queryTokens(query: string): string[] {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= MIN_TOKEN_LENGTH);
  return [...new Set(words)];
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Splits `text` into alternating plain and matching parts. Matching is case-insensitive and starts only
 * at the beginning of a word, so "late" marks "late" and "lateness" but not the middle of "calculated";
 * longer tokens are tried first so a token that contains another is marked whole.
 */
export function highlightParts(text: string, tokens: readonly string[]): HighlightPart[] {
  if (text === '' || tokens.length === 0) return [{ text, match: false }];

  // Every token is escaped above, so the pattern is a plain alternation of literals.
  // eslint-disable-next-line security/detect-non-literal-regexp
  const pattern = new RegExp(
    `(?<![\\p{L}\\p{N}_])(${[...tokens]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|')})`,
    'giu'
  );

  const parts: HighlightPart[] = [];
  let cursor = 0;
  for (const found of text.matchAll(pattern)) {
    const start = found.index ?? 0;
    if (start > cursor) parts.push({ text: text.slice(cursor, start), match: false });
    parts.push({ text: found[0], match: true });
    cursor = start + found[0].length;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
  return parts.length > 0 ? parts : [{ text, match: false }];
}
